import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface Recording {
  recordingId: string;
  s3Key: string;
  interviewName: string;
  sessionId?: string;
  interviewType?: string;
  fileSize?: number;
  duration?: number;
  mimeType?: string;
  createdAt: string;
}

interface RecordingsListProps {
  username: string;
}

const RecordingsList: React.FC<RecordingsListProps> = ({ username }) => {
  const { getToken } = useAuth();
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [playingRecording, setPlayingRecording] = useState<Recording | null>(
    null
  );
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [loadingPlayback, setLoadingPlayback] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Recordings (S3) use the main deployed API even when running locally
  const API_URL =
    import.meta.env.VITE_RAG_API_URL ||
    import.meta.env.VITE_API_URL ||
    'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com';

  const fetchRecordings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_URL}/recordings?username=${encodeURIComponent(username)}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch recordings');
      }

      const data = await response.json();
      setRecordings(data.recordings || []);
    } catch (err) {
      console.error('Error fetching recordings:', err);
      setError('Failed to load recordings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getToken, username]);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  const getRecordingUrl = async (
    recordingId: string
  ): Promise<string | null> => {
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_URL}/recordings/${recordingId}/download?username=${encodeURIComponent(
          username
        )}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get recording URL');
      }

      const data = await response.json();
      return data.downloadUrl;
    } catch (err) {
      console.error('Error getting recording URL:', err);
      return null;
    }
  };

  const handleDownload = async (recordingId: string) => {
    setDownloadingId(recordingId);
    try {
      const url = await getRecordingUrl(recordingId);
      if (url) {
        // Open download URL in new tab
        window.open(url, '_blank');
      } else {
        alert('Failed to download recording. Please try again.');
      }
    } catch (err) {
      console.error('Error downloading recording:', err);
      alert('Failed to download recording. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (recordingId: string, recordingName: string) => {
    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete "${
        recordingName || 'this recording'
      }"?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(recordingId);
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_URL}/recordings/${recordingId}?username=${encodeURIComponent(
          username
        )}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete recording');
      }

      // Remove from local state
      setRecordings((prev) =>
        prev.filter((r) => r.recordingId !== recordingId)
      );
      console.log(`✅ Recording ${recordingId} deleted successfully`);
    } catch (err) {
      console.error('Error deleting recording:', err);
      alert(
        `Failed to delete recording: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handlePlay = async (recording: Recording) => {
    setLoadingPlayback(true);
    setPlayingRecording(recording);
    try {
      const url = await getRecordingUrl(recording.recordingId);
      if (url) {
        setPlaybackUrl(url);
      } else {
        alert('Failed to load recording. Please try again.');
        setPlayingRecording(null);
      }
    } catch (err) {
      console.error('Error loading recording for playback:', err);
      alert('Failed to load recording. Please try again.');
      setPlayingRecording(null);
    } finally {
      setLoadingPlayback(false);
    }
  };

  const closePlayback = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
    }
    setPlayingRecording(null);
    setPlaybackUrl(null);
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown duration';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-slate-600">
        <p>Loading recordings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-semibold mb-2">Error</p>
          <p>{error}</p>
          <button
            onClick={fetchRecordings}
            className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div className="p-6 text-center text-slate-600">
        <p className="text-lg mb-2">No recordings found</p>
        <p className="text-sm">
          Recordings will appear here after you submit them from an interview
          session.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-slate-800">My Recordings</h2>
        <button
          onClick={fetchRecordings}
          className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm"
        >
          🔄 Refresh
        </button>
      </div>

      <div className="space-y-3">
        {recordings.map((recording) => (
          <div
            key={recording.recordingId}
            className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800 mb-1">
                  {recording.interviewName || 'Unnamed Interview'}
                </h3>
                <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                  {recording.interviewType && (
                    <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
                      {recording.interviewType}
                    </span>
                  )}
                  <span>{formatFileSize(recording.fileSize)}</span>
                  {recording.duration && (
                    <span>Duration: {formatDuration(recording.duration)}</span>
                  )}
                  <span>{formatDate(recording.createdAt)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePlay(recording)}
                  className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm whitespace-nowrap"
                >
                  ▶️ Play
                </button>
                <button
                  onClick={() => handleDownload(recording.recordingId)}
                  disabled={downloadingId === recording.recordingId}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                >
                  {downloadingId === recording.recordingId
                    ? '⏳...'
                    : '⬇️ Download'}
                </button>
                <button
                  onClick={() =>
                    handleDelete(recording.recordingId, recording.interviewName)
                  }
                  disabled={deletingId === recording.recordingId}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                  title="Delete recording"
                >
                  {deletingId === recording.recordingId ? '⏳...' : '🗑️ Delete'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Video Playback Modal */}
      {playingRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">
                  {playingRecording.interviewName || 'Recording'}
                </h3>
                <p className="text-sm text-slate-500">
                  {formatDate(playingRecording.createdAt)}
                  {playingRecording.duration &&
                    ` • ${formatDuration(playingRecording.duration)}`}
                </p>
              </div>
              <button
                onClick={closePlayback}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                title="Close"
              >
                <svg
                  className="w-6 h-6 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Video Player */}
            <div className="bg-black">
              {loadingPlayback ? (
                <div className="flex items-center justify-center h-64 sm:h-96">
                  <div className="text-white text-lg">Loading video...</div>
                </div>
              ) : playbackUrl ? (
                <video
                  ref={videoRef}
                  src={playbackUrl}
                  controls
                  autoPlay
                  className="w-full max-h-[70vh]"
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <div className="flex items-center justify-center h-64 sm:h-96">
                  <div className="text-white text-lg">Failed to load video</div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => handleDownload(playingRecording.recordingId)}
                disabled={downloadingId === playingRecording.recordingId}
                className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 text-sm"
              >
                {downloadingId === playingRecording.recordingId
                  ? '⏳ Downloading...'
                  : '⬇️ Download'}
              </button>
              <button
                onClick={() => {
                  handleDelete(
                    playingRecording.recordingId,
                    playingRecording.interviewName
                  );
                  closePlayback();
                }}
                disabled={deletingId === playingRecording.recordingId}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
              >
                {deletingId === playingRecording.recordingId
                  ? '⏳ Deleting...'
                  : '🗑️ Delete'}
              </button>
              <button
                onClick={closePlayback}
                className="px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingsList;
